import App from "./App";
import { ErrorFallback } from "@components/error-fallback/error-fallback.component";
import { SignupPage } from "./pages/auth/signup.page";
import { TooltipProvider } from "./components/ui/tooltip";
import { OnboardingPage } from "./pages/auth/onboarding.page";
import { AccountPage } from "./pages/account.page";
import { FullDashboardPage } from "./pages/dashboard/full-dashboard.page";
import { CommDashboardPage } from "./pages/dashboard/comm-dashboard.page";
import { UserManagementPage } from "./pages/user-management.page";
import { JobPage } from "./pages/job-page/job.page";
import { ClientPage } from "./pages/client-page/client.page";
import { ArchivePage } from "./pages/archive/archive.page";
import { PdfTemplateConfiguration } from "./pages/pdf-template-configuration/pdf-template-configuration.page";
import { ClientDashboardPage } from "./pages/client-dashboard-page/client-dashboard.page";
import { createBrowserRouter, redirect } from "react-router-dom";
import { SheetContentType, useSheetStore } from "./store/Sheets/sheet.store";
import { MonthlyInvoicesPage } from "./pages/monthly-invoices/monthly-invoices.page";
import { SupportPage } from "./pages/support/support.page";
import { MigrateAmountsPage } from "./pages/support/migrate-amounts/migrate-amounts.page";
import { PdfExportPage } from "./pages/support/pdf-export/pdf-export.page";
import { PdfDiffPage } from "./pages/support/pdf-diff/pdf-diff.page";
import { QrTesterPage } from "./pages/support/qr-tester/qr-tester.page";
import { EmailSenderPage } from "./pages/support/email-sender/email-sender.page";
import { StorageCleanupPage } from "./pages/support/storage-cleanup/storage-cleanup.page";
import { DebitorenExportPage } from "./pages/support/debitoren-export/debitoren-export.page";
import { BlobToPdfPage } from "./pages/support/blob-to-pdf/blob-to-pdf.page";
import { DpiSimulatorPage } from "./pages/support/dpi-simulator/dpi-simulator.page";
import { StatisticsPage } from "./pages/statistics/statistics.page";
import { NotFoundPage } from "./404.page";
import { useJobStore } from "./store/Jobs";
import { useCentralStore } from "./store/Central";
import { useArchiveStore } from "./store/Archive";
import { AgendaPage } from "./pages/agenda/agenda.page";
import { DebitorenPage } from "./pages/debitoren/debitoren.page";
import { FakturajournalPage } from "./pages/debitoren/fakturajournal.page";
import { OffenePostenPage } from "./pages/debitoren/offene-posten/offene-posten.page";
import { AkontoPage } from "./pages/debitoren/akonto/akonto.page";
import { VerlustPage } from "./pages/debitoren/verlust/verlust.page";
import { MahnwesenPage } from "./pages/debitoren/mahnwesen/mahnwesen.page";
import { RueckverguetungPage } from "./pages/debitoren/rueckverguetung/rueckverguetung.page";
import { StornojournalPage } from "./pages/debitoren/stornojournal/stornojournal.page";
import { ZahlungsjournalPage } from "./pages/debitoren/zahlungsjournal.page";
import { MigrationPage } from "./pages/migration/migration.page";
import { InviteeSignup } from "./pages/auth/invitee_signup.page";
import { JobDocumentTemplatesPage } from "./pages/job-document-templates/job-document-templates.page";
import { CatalogsPage } from "./pages/catalogs/catalogs.page";
import { PreislistenPage } from "./pages/organization/subpages/preislisten.page";
import { PasswordRecoveryPage } from "./pages/password-recovery/password-recovery.page";
import { SetPasswordPage } from "./pages/password-recovery/set-password.page";
import { ArticlesOverviewPage } from "./pages/article/articles-overview.page";
import { JobsArchivePage } from "./pages/jobs/jobs-archive.page";
import { RecycleBinPage } from "./pages/recycle-bin/recycle-bin.page";
import { FormBuilderApp } from "./pages/form-builder/FormBuilderApp";
import { FormsList } from "./pages/form-builder/FormsList";
import { FormsPage } from "./pages/form-builder/forms.page";
import { VendorDetailPageConnected } from "./pages/vendors/connected/vendor-detail-connected.page";
import { VendorJobPageConnected } from "./pages/vendors/connected/vendor-job-connected.page";
import { VendorJobRequestPageConnected } from "./pages/vendors/connected/vendor-job-request-connected.page";
import { VendorJobRequestChannelPageConnected } from "./pages/vendors/connected/vendor-job-request-channel-connected.page";
import { VendorGeneralChatPageConnected } from "./pages/vendors/connected/vendor-general-chat-connected.page";
import { VendorInvoicesPageConnected } from "./pages/vendors/connected/vendor-invoices-connected.page";
import { VendorVerrechnetPageConnected } from "./pages/vendors/connected/vendor-verrechnet-connected.page";
import { VendorArchivPageConnected } from "./pages/vendors/connected/vendor-archiv-connected.page";
import { VendorDashboardPageConnected } from "./pages/vendors/connected/vendor-dashboard-connected.page";
import { ChatsPage } from "./pages/chats/chats.page";
import { JobsBookedPage } from "./pages/jobs/jobs-booked.page";
import { JobsPaidPage } from "./pages/jobs/jobs-paid.page";
import { PatientMergePage } from "./pages/patient-merge/patient-merge.page";
import { JobTabsConfigurationPage } from "./pages/job-configuration/job-tabs-configuration.page";
import { ServiceCatalogPage } from "./pages/service-catalog/service-catalog.page";
import { VendorSigninPage } from "./pages/auth/vendor-signin.page";
import { useMemo } from "react";
import { OrganizationPage } from "./pages/organization/organization.page";
import { OrganizationOverviewPage } from "./pages/organization/organization-overview.page";
import { GarantenVerwaltenPage } from "./pages/organization/subpages/administration/garanten.page";
import { KonfigurationseinstellungenPage } from "./pages/organization/subpages/administration/konfigurationseinstellungen.page";
import { ClientSettingsPage } from "./pages/client-page/subpages/settings/client-settings.page";
import { AllgemeineInformationenPage } from "./pages/client-page/subpages/settings/allgemeine-informationen.page";
import { RechnungenPage } from "./pages/client-page/subpages/settings/rechnungen.page";
import { BehandlerlistePage } from "./pages/client-page/subpages/settings/behandlerliste.page";
import { VerbindungPage } from "./pages/client-page/subpages/settings/verbindung.page";
import { AbonnementPage } from "./pages/organization/subpages/administration/abonnement.page";
import { InaktiveKundenPage } from "./pages/organization/subpages/inaktive-kunden.page";
import { AdministrationPage } from "./pages/organization/subpages/administration.page";
import { FirmenangabenPage as FirmenangabenAdminPage } from "./pages/organization/subpages/administration/firmenangaben.page";
import { RechnungswesenPage as RechnungswesenAdminPage } from "./pages/organization/subpages/administration/rechnungswesen.page";
import { MahnungswesenPage as MahnungswesenAdminPage } from "./pages/organization/subpages/administration/mahnungswesen.page";
import { RegionenAdminPage } from "./pages/organization/subpages/administration/regionen.page";
import { KontenAdminPage } from "./pages/organization/subpages/administration/konten.page";
import { DatenbankSicherungPage } from "./pages/organization/subpages/administration/datenbank-sicherung.page";
import { Navigate } from "react-router-dom";
import { ClientChatsPage } from "./pages/chats/client-chats.page";
import { ClientGeneralChatPage } from "./pages/chats/client-general-chat.page";
import { ClientJobRequestPage } from "./pages/client-page/client-job-request.page";
import { KundeDokumenteneinstellungenPage } from "./pages/client-page/subpages/settings/kunde-dokumenteneinstellungen.page";
import { RolesEnum } from "@/lib/types/roles.types";
import { useAttributesStore } from "./store/Attributes";
import { MIGRATION_DECISION } from "./constants/onboarding";
import { hasFullFeatureSet } from "./store/Central/selectors";
import { CourierDashboardPage } from "./pages/courier/courier-dashboard.page";
import { CourierDeliveryDetailPage } from "./pages/courier/courier-delivery-detail.page";
import { DeliveriesPage } from "./pages/deliveries/deliveries.page";
import { addRecent } from "@/lib/utils/recents";
import { getClientDisplayName } from "@/lib/utils/client-utils";
import { useConnectStore } from "./store/Connect";
import { AuftraegeOverviewPage } from "./pages/auftraege/auftraege-overview.page";
import { DruckerPage } from "./pages/organization/subpages/drucker/drucker.page";
import { DruckerauftraegePage } from "./pages/organization/subpages/administration/druckerauftraege.page";

const IndexPage: React.FC = () => {
	const role = useCentralStore((state) => state.role);
	const subscriptionTier = useCentralStore((state) => state.subscriptionTier);
	if (role === RolesEnum.COURIER) return <CourierDashboardPage />;
	if (hasFullFeatureSet(subscriptionTier)) return <FullDashboardPage />;
	return <CommDashboardPage />;
};

const WrappedChatsPage: React.FC = () => {
	const { organization, userId, clients } = useCentralStore((state) => ({
		organization: state.organization,
		userId: state.profile?.id,
		clients: Object.values(state.clientsLookup),
	}));
	const { shareFile, unshareFile, acceptJobRequest, denyJobRequest } =
		useJobStore((state) => ({
			shareFile: state.shareFile,
			unshareFile: state.unshareFile,
			acceptJobRequest: state.acceptJobRequest,
			denyJobRequest: state.denyJobRequest,
		}));
	const clientOrganizationIds = useMemo(() => {
		return clients
			.map((client) => client.connected_organization_id)
			.filter((id): id is string => id !== null);
	}, [clients]);

	if (!organization?.id || !userId) {
		return <div></div>;
	}

	return (
		<ChatsPage
			organizationId={organization.id}
			onShareFile={shareFile}
			onUnshareFile={unshareFile}
			onAcceptJobRequest={acceptJobRequest}
			onDenyJobRequest={denyJobRequest}
			userId={userId}
			clientOrganizationIds={clientOrganizationIds}
		/>
	);
};

/** The archive queries need the organisation, which a direct page load has not resolved yet. */
const loadArchive = async (scope: string | null) => {
	const state = useCentralStore.getState();
	if (!state.initialized) {
		await state.initialize();
	}
	// Not awaited on purpose: the page renders its loading state while the list streams in.
	useArchiveStore.getState().setArchiveScope(scope);
	return null;
};

export const router = createBrowserRouter([
	{
		path: "/",
		element: <App />,
		errorElement: <ErrorFallback variant="full" />,
		loader: async ({
			params,
			request,
		}: {
			params: { clientId?: string };
			request: Request;
		}) => {
			const state = useCentralStore.getState();
			const url = new URL(request.url);

			if (!state.initialized) {
				const loggedIn = await state.getSession();
				if (!loggedIn) return null;

				const role = useCentralStore.getState().role;
				if (role === RolesEnum.COURIER) {
					await useCentralStore.getState().initCourier();
					return null;
				}
			}

			await useCentralStore.getState().initialize();

			const role = useCentralStore.getState().role;
			if (role === RolesEnum.COURIER) return null;

			const isOwner = role === RolesEnum.OWNER;

			// Check: Chose migration but hasn't completed it
			const migrationDecision = useAttributesStore
				.getState()
				.getOnboardingAttribute("onboarding_migration_decision");
			if (
				isOwner &&
				migrationDecision === MIGRATION_DECISION.MIGRATE_FROM_DLXP &&
				!url.pathname.includes("/migration")
			) {
				window.location.href = "/migration";
				return null;
			}

			// The initial load is not caught by the other loaders
			// so we cover the case where a user goes to a client/job page directly here
			// Job routes switch the client themselves; a parallel switch here would
			// supersede theirs via clientFetchEpoch and the job would never load.
			if (params.clientId && !url.pathname.includes("/job/")) {
				await useCentralStore.getState().switchClient(params.clientId);
			}
			return null;
		},
		children: [
			{
				path: "/",
				element: <IndexPage />,
			},
			{
				path: "/auftraege",
				element: <AuftraegeOverviewPage />,
			},
			{
				path: "/lieferung/:deliveryId",
				element: <CourierDeliveryDetailPage />,
			},
			{
				path: "/migration",
				element: <MigrationPage />,
			},
			{
				path: "/chats",
				element: <WrappedChatsPage />,
			},
			{
				path: "/chats/:channelId",
				element: <WrappedChatsPage />,
			},
			{
				path: "/organisation",
				element: <OrganizationPage />,
				errorElement: <ErrorFallback variant="inline" />,
				children: [
					{
						path: "",
						element: <OrganizationOverviewPage />,
					},
					{
						path: "administration",
						element: <AdministrationPage />,
						children: [
							{
								path: "firmenangaben",
								element: <FirmenangabenAdminPage />,
							},
							{
								path: "rechnungswesen",
								element: <RechnungswesenAdminPage />,
							},
							{
								path: "konten",
								element: <KontenAdminPage />,
							},
							{
								path: "mahnungswesen",
								element: <MahnungswesenAdminPage />,
							},
							{
								path: "regionen",
								element: <RegionenAdminPage />,
							},
							{
								path: "garanten",
								element: <GarantenVerwaltenPage />,
							},
							{
								path: "konfigurationseinstellungen",
								element: <KonfigurationseinstellungenPage />,
							},
							{
								path: "abonnement",
								element: <AbonnementPage />,
							},
							{
								path: "datenbank-sicherung",
								element: <DatenbankSicherungPage />,
							},
							{
								path: "benutzerverwaltung",
								element: <UserManagementPage />,
							},
							{
								path: "preislisten",
								element: <PreislistenPage />,
							},
							{
								path: "inaktive-kunden",
								element: <InaktiveKundenPage />,
							},
							{
								path: "drucker",
								element: <DruckerPage />,
							},
							{
								path: "druckerauftraege",
								element: <DruckerauftraegePage />,
							},
						],
					},
					{
						path: "pdf-gestaltung",
						element: <PdfTemplateConfiguration />,
					},
					{
						path: "abrechnung",
						element: (
							<Navigate
								to="/organisation/administration/abonnement"
								replace
							/>
						),
					},
					{
						path: "inaktive-kunden",
						element: (
							<Navigate
								to="/organisation/administration/inaktive-kunden"
								replace
							/>
						),
					},
					{
						path: "vorlagen-jumbos",
						element: <JobDocumentTemplatesPage />,
					},
					{
						path: "auftragsformulare",
						element: <Navigate to="/auftragsformulare" replace />,
					},
					{
						path: "auftragsformulare/:formId",
						element: <Navigate to="/auftragsformulare" replace />,
					},
					{
						path: "leistungen",
						element: <CatalogsPage />,
					},
					{
						path: "preislisten",
						element: (
							<Navigate
								to="/organisation/administration/preislisten"
								replace
							/>
						),
					},
					{
						path: "artikel",
						element: <ArticlesOverviewPage />,
					},
					{
						// Article detail lives in a sheet — deep links open
						// it on top of the overview.
						path: "artikel/:id",
						loader: ({ params }) => {
							if (params.id) {
								useSheetStore
									.getState()
									.openSheet(SheetContentType.ARTICLE, {
										articleId: Number(params.id),
									});
							}
							return redirect("/organisation/artikel");
						},
					},
					{
						path: "drucker",
						element: (
							<Navigate
								to="/organisation/administration/drucker"
								replace
							/>
						),
					},
					{
						path: "leistungskatalog",
						element: <ServiceCatalogPage />,
					},
					{
						path: "auftragsansichten",
						element: <JobTabsConfigurationPage />,
					},
					{
						path: "migration",
						element: <MigrationPage />,
					},
				],
			},
			{
				path: `client/:clientId`,
				element: <ClientPage />,
				errorElement: <ErrorFallback variant="inline" />,
				loader: async ({ params, request }) => {
					const clientId = params.clientId!;
					// Check if this is a navigation to a job page
					// If so, let the job loader handle the combined operation
					const url = new URL(request.url);
					const isJobNavigation = url.pathname.includes("/job/");

					if (isJobNavigation) {
						return null;
					}

					const client = await useCentralStore
						.getState()
						.switchClient(clientId);
					if (client) {
						addRecent({
							type: "client",
							id: clientId,
							name: getClientDisplayName(client),
							code: client.code ?? null,
							timestamp: Date.now(),
						});
					}
					return null;
				},
				children: [
					{
						path: "/client/:clientId",
						element: <ClientDashboardPage />,
					},
					{
						// For behaviors that should happen specifically when switching jobs, put the logic inside handleJobRouteChange in the job store.
						path: "job/:jobId",
						element: <JobPage />,
						loader: async ({ params, request }) => {
							const jobId = params.jobId!;
							// Extract clientId from the URL path
							const url = new URL(request.url);
							const pathMatch = url.pathname.match(
								/\/client\/([^\/]+)\/job/
							);
							const urlClientId = pathMatch ? pathMatch[1] : null;

							// Ensure central store is initialized before proceeding
							const state = useCentralStore.getState();
							if (!state.initialized) {
								await state.initialize();
							}

							const currentClient =
								useCentralStore.getState().client;

							// Check if we need to switch clients and load job together
							if (
								urlClientId &&
								(!currentClient ||
									currentClient.id !== urlClientId)
							) {
								// Use the combined function to avoid double re-renders
								await useCentralStore
									.getState()
									.switchClientAndLoadJob(urlClientId, jobId);
							} else {
								// Client is already selected, just load the job
								await useJobStore
									.getState()
									.handleJobRouteChange(jobId);
							}

							return null;
						},
					},
					{
						path: "archive",
						element: <JobsArchivePage />,
					},
					{
						path: "dateiarchiv",
						element: <ArchivePage />,
						loader: ({ params }) => loadArchive(params.clientId!),
					},
					{
						path: "booked",
						element: <JobsBookedPage />,
					},
					{
						path: "paid",
						element: <JobsPaidPage />,
					},
					{
						path: "chats",
						element: <ClientChatsPage />,
					},
					{
						path: "chats/:channelId",
						element: <ClientChatsPage />,
					},
					{
						path: "general-chat",
						element: <ClientGeneralChatPage />,
					},
					{
						path: "job/request/:channelId",
						// The parent client/:clientId loader skips switchClient() for /job/ paths,
						// expecting the child loader to handle it (same pattern as job/:jobId loader above).
						loader: async ({ request }: { request: Request }) => {
							const url = new URL(request.url);
							const pathMatch = url.pathname.match(
								/\/client\/([^\/]+)\/job/
							);
							const urlClientId = pathMatch ? pathMatch[1] : null;

							const state = useCentralStore.getState();
							if (!state.initialized) {
								await state.initialize();
							}

							const currentClient =
								useCentralStore.getState().client;

							if (
								urlClientId &&
								(!currentClient ||
									currentClient.id !== urlClientId)
							) {
								await useCentralStore
									.getState()
									.switchClient(urlClientId);
							}

							useJobStore.getState().clearCurrentJob();
							return null;
						},
						element: <ClientJobRequestPage />,
					},
					{
						path: "einstellungen",
						element: <ClientSettingsPage />,
						children: [
							{
								path: "allgemeine-informationen",
								element: <AllgemeineInformationenPage />,
							},
							{
								path: "rechnungen",
								element: <RechnungenPage />,
							},
							{
								path: "behandlerliste",
								element: <BehandlerlistePage />,
							},
							{
								path: "verbindung",
								element: <VerbindungPage />,
							},
							{
								path: "dokumente",
								element: <KundeDokumenteneinstellungenPage />,
							},
						],
					},
				],
			},
			{
				path: "account-settings",
				element: <AccountPage />,
			},
			{
				path: "user-management",
				element: <UserManagementPage />,
			},
			{
				path: "/archive",
				element: <ArchivePage />,
				loader: ({ request }: { request: Request }) =>
					loadArchive(
						new URL(request.url).searchParams.get("clientId")
					),
			},
			{
				path: "/document-settings",
				element: <Navigate to="/organisation/pdf-gestaltung" replace />,
			},
			{
				path: "/monthly-invoices",
				element: <MonthlyInvoicesPage />,
			},
			{
				path: "/support",
				element: <SupportPage />,
			},
			{
				path: "/support/migrate-amounts",
				element: <MigrateAmountsPage />,
			},
			{
				path: "/support/pdf-export",
				element: <PdfExportPage />,
			},
			{
				path: "/support/pdf-diff",
				element: <PdfDiffPage />,
			},
			{
				path: "/support/qr-tester",
				element: <QrTesterPage />,
			},
			{
				path: "/support/email-sender",
				element: <EmailSenderPage />,
			},
			{
				path: "/support/storage-cleanup",
				element: <StorageCleanupPage />,
			},
			{
				path: "/support/debitoren-export",
				element: <DebitorenExportPage />,
			},
			{
				path: "/support/blob-to-pdf",
				element: <BlobToPdfPage />,
			},
			{
				path: "/support/dpi-simulator",
				element: <DpiSimulatorPage />,
			},
			{
				path: "/job-document-templates",
				element: (
					<Navigate to="/organisation/vorlagen-jumbos" replace />
				),
			},
			{
				path: "/statistics",
				element: <StatisticsPage />,
			},
			{
				path: "/agenda",
				element: <AgendaPage />,
			},
			{
				path: "/debitoren",
				element: <DebitorenPage />,
				errorElement: <ErrorFallback variant="inline" />,
				children: [
					{
						path: "",
						element: <Navigate to="fakturajournal" replace />,
					},
					{
						path: "fakturajournal",
						element: <FakturajournalPage />,
					},
					{
						path: "offene-posten",
						element: <OffenePostenPage />,
					},
					{
						path: "stornojournal",
						element: <StornojournalPage />,
					},
					{
						path: "zahlungsjournal",
						element: <ZahlungsjournalPage />,
					},
					{
						path: "akontoansicht",
						element: <AkontoPage />,
					},
					{
						path: "verlustansicht",
						element: <VerlustPage />,
					},
					{
						path: "rueckverguetungen",
						element: <RueckverguetungPage />,
					},
					{
						path: "mahnwesen",
						element: <MahnwesenPage />,
					},
				],
			},
			{
				path: "/abonnement",
				element: (
					<Navigate
						to="/organisation/administration/abonnement"
						replace
					/>
				),
			},
			{
				path: "/catalogs",
				element: <Navigate to="/organisation/leistungen" replace />,
			},
			{
				path: "/lieferungen",
				element: <DeliveriesPage />,
			},
			{
				path: "/recycle-bin",
				element: <RecycleBinPage />,
			},
			{
				path: "/auftragsformulare",
				element: <FormsPage />,
				children: [
					{
						index: true,
						element: <FormsList />,
					},
					{
						path: ":formId",
						element: <FormBuilderApp />,
					},
				],
			},
			{
				path: "/vendor/:vendorId",
				element: <VendorDetailPageConnected />,
				errorElement: <ErrorFallback variant="inline" />,
				loader: ({ params }) => {
					const vendorId = params.vendorId!;
					const vendor =
						useConnectStore.getState().brandsByOrgId[vendorId];
					if (vendor) {
						addRecent({
							type: "vendor",
							orgId: vendorId,
							name: vendor.name,
							timestamp: Date.now(),
						});
					}
					return null;
				},
				children: [
					{
						index: true,
						element: <VendorDashboardPageConnected />,
					},
					{
						path: "chat",
						element: <VendorGeneralChatPageConnected />,
					},
					{
						path: "job/:jobId",
						element: <VendorJobPageConnected />,
					},
					{
						path: "invoices",
						element: <VendorInvoicesPageConnected />,
					},
					{
						path: "job-request",
						element: <VendorJobRequestPageConnected />,
					},
					{
						path: "job/request/:channelId",
						element: <VendorJobRequestChannelPageConnected />,
					},
					{
						path: "verrechnet",
						element: <VendorVerrechnetPageConnected />,
					},
					{
						path: "archiv",
						element: <VendorArchivPageConnected />,
					},
				],
			},
			{
				path: "/patienten-zusammenlegen/:patientId",
				element: <PatientMergePage />,
			},
		],
	},
	{
		path: "registrierung",
		element: (
			<TooltipProvider delayDuration={150}>
				<SignupPage />
			</TooltipProvider>
		),
		loader: async () => {
			await useCentralStore.getState().initialize();
			const profile = useCentralStore.getState().profile;
			if (profile?.id) {
				const hasCompleted = useAttributesStore
					.getState()
					.hasCompletedOnboarding();
				window.location.href = hasCompleted ? "/" : "/onboarding";
				return null;
			}
			return null;
		},
	},
	{
		path: "onboarding",
		element: (
			<TooltipProvider delayDuration={150}>
				<OnboardingPage />
			</TooltipProvider>
		),
		loader: async () => {
			await useCentralStore.getState().initialize();
			const profile = useCentralStore.getState().profile;
			if (!profile?.id) {
				window.location.href = "/registrierung";
				return null;
			}
			return null;
		},
	},
	{
		path: "invite",
		element: <InviteeSignup />,
	},
	{
		path: "kommunikation-login",
		element: <VendorSigninPage />,
	},
	{
		path: "password-recovery",
		element: <PasswordRecoveryPage />,
	},
	{
		path: "set-password",
		element: <SetPasswordPage />,
	},
	{
		path: "*",
		element: <NotFoundPage />,
	},
]);
